// Engineer impersonation Zustand store (spec 048).
//
// The store holds the live grant state that drives every read overlay
// in the app: the banner, identity overrides, header attach, and the
// dev toolkit. Persisted slice goes to **sessionStorage** only —
// localStorage and cookies are explicitly forbidden by FR-039 and
// FR-066 (no cross-tab sharing, no Chrome Sync replication).
//
// Lifecycle:
//   - `setActive(payload)`  — called by `useImpersonation().activate`
//     after a successful mint. Creates a fresh AbortController so
//     in-flight impersonated requests can be aborted in one shot
//     during teardown. Emits `engineer_impersonation_started`.
//   - `setInactive(reason)` — clears every field, removes the
//     sessionStorage key, aborts the controller. Emits
//     `engineer_impersonation_stopped`.
//   - `updateLastUsed(ts)`  — bumped by the future ttl-monitor when
//     the backend acknowledges a request via the impersonation
//     header.
//
// The persisted slice is intentionally a strict subset of the live
// state — `isActive` is recomputed from `grantToken !== null` on
// rehydrate (so a stale persist with no token can never appear
// active), and `abortController` lives only in memory.

import type {
  ImpersonationActivatePayload,
  ImpersonationState,
} from '@morfi/backend-sdk-v2/client';
import type { StoreApi, UseBoundStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { onEvent } from '@/events';
import { createTrackedStore } from '@/lib/dev/zustand-registry';
import type { Network } from '@/lib/network';

export const IMPERSONATION_PERSIST_KEY = 'morfi-impersonation-grant';

interface ImpersonationStore extends ImpersonationState {
  /** In-memory only — never persisted. Created in `setActive`,
   *  aborted by `setInactive` and the ttl-monitor. */
  abortController: AbortController | null;
  /** Engineer's user id — held only for the duration of the active
   *  session so analytics events can attribute correctly. Never
   *  persisted. */
  engineerUserId: string | null;
  /** SHA-256(targetUserId).slice(0,12). Persisted alongside the
   *  grant so analytics can correlate `_started` / `_stopped`
   *  pairs. Never the raw target id. */
  targetUserIdHash: string | null;

  setActive: (payload: ImpersonationActivatePayload) => void;
  setInactive: (
    reason: 'engineer_stop' | 'expired' | 'network_toggle' | 'invalidated' | 'signout',
  ) => void;
  updateLastUsed: (ts: number) => void;
}

const INITIAL_STATE: Pick<
  ImpersonationStore,
  | 'isActive'
  | 'grantToken'
  | 'grantId'
  | 'network'
  | 'target'
  | 'issuedAt'
  | 'expiresAt'
  | 'lastUsedAt'
  | 'idleTtlSeconds'
  | 'abortController'
  | 'engineerUserId'
  | 'targetUserIdHash'
> = {
  isActive: false,
  grantToken: null,
  grantId: null,
  network: null,
  target: null,
  issuedAt: null,
  expiresAt: null,
  lastUsedAt: null,
  idleTtlSeconds: null,
  abortController: null,
  engineerUserId: null,
  targetUserIdHash: null,
};

/** Slice that gets serialised to sessionStorage. PII-tight by
 *  construction — never include the engineer's email or the raw
 *  target user id in any field that is not already inside `target`
 *  (which is needed to render the banner offline). */
type PersistedSlice = Pick<
  ImpersonationStore,
  | 'grantToken'
  | 'grantId'
  | 'network'
  | 'target'
  | 'issuedAt'
  | 'expiresAt'
  | 'idleTtlSeconds'
  | 'targetUserIdHash'
>;

export const useImpersonationStore: UseBoundStore<StoreApi<ImpersonationStore>> =
  createTrackedStore<ImpersonationStore>('useImpersonationStore')(
    persist(
      (set, get) => ({
        ...INITIAL_STATE,

        setActive: (payload: ImpersonationActivatePayload): void => {
          // Abort any in-flight requests from a previous session
          // before creating the new controller. Defensive — the
          // mint endpoint atomically replaces a previous grant
          // server-side, but client state could lag.
          const existing = get().abortController;
          if (existing !== null) {
            existing.abort();
          }
          const controller = new AbortController();

          set({
            isActive: true,
            grantToken: payload.grantToken,
            grantId: payload.grantId,
            network: payload.network,
            target: payload.target,
            issuedAt: payload.issuedAt,
            expiresAt: payload.expiresAt,
            lastUsedAt: payload.issuedAt,
            idleTtlSeconds: payload.idleTtlSeconds,
            abortController: controller,
            engineerUserId: payload.engineerUserId,
            targetUserIdHash: payload.targetUserIdHash,
          });

          onEvent('engineer_impersonation_started', {
            engineerUserId: payload.engineerUserId,
            targetUserIdHash: payload.targetUserIdHash,
            network: payload.network as Network,
          });
        },

        setInactive: (
          reason: 'engineer_stop' | 'expired' | 'network_toggle' | 'invalidated' | 'signout',
        ): void => {
          const prev = get();
          if (prev.abortController !== null) {
            prev.abortController.abort();
          }

          // Emit the stopped event BEFORE we clear identity fields
          // so the payload is well-formed.
          if (
            prev.engineerUserId !== null &&
            prev.targetUserIdHash !== null &&
            prev.network !== null
          ) {
            onEvent('engineer_impersonation_stopped', {
              engineerUserId: prev.engineerUserId,
              targetUserIdHash: prev.targetUserIdHash,
              network: prev.network as Network,
              reason,
            });
          }

          set({ ...INITIAL_STATE });

          // Belt-and-braces: nuke the persisted entry directly. The
          // persist middleware's partialize would already strip
          // `grantToken` on the next write, but explicit removal
          // ensures no value lingers if something else short-circuits
          // the middleware.
          if (typeof sessionStorage !== 'undefined') {
            try {
              sessionStorage.removeItem(IMPERSONATION_PERSIST_KEY);
            } catch {
              // SILENT-CATCH: sessionStorage may be blocked (e.g. SSR or restricted privacy mode).
            }
          }
        },

        updateLastUsed: (ts: number): void => {
          set({ lastUsedAt: ts });
        },
      }),
      {
        name: IMPERSONATION_PERSIST_KEY,
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state: ImpersonationStore): PersistedSlice => ({
          grantToken: state.grantToken,
          grantId: state.grantId,
          network: state.network,
          target: state.target,
          issuedAt: state.issuedAt,
          expiresAt: state.expiresAt,
          idleTtlSeconds: state.idleTtlSeconds,
          targetUserIdHash: state.targetUserIdHash,
        }),
        // After rehydrate from sessionStorage, derive `isActive` from
        // whether a grant token is actually present. A persisted slice
        // missing the token (older session, manual storage edit) MUST
        // NOT register as active.
        onRehydrateStorage:
          () =>
          // ALLOW-UNKNOWN: type-narrowed at use-site / mock signature passthrough.
          (state: ImpersonationStore | undefined, error: unknown): void => {
            if (error !== undefined && error !== null) return;
            if (state === undefined || state === null) return;
            state.isActive = state.grantToken !== null && state.network !== null;
            // Restore lastUsedAt from issuedAt as a safe lower bound;
            // ttl-monitor will refresh it on the next request.
            if (state.lastUsedAt === null && state.issuedAt !== null) {
              state.lastUsedAt = state.issuedAt;
            }
            // Backfill new target fields if the persisted slice was
            // written by an older build that didn't have them. Without
            // this, `target.connectedProviders.map(...)` crashes downstream
            // and the `nsfw=` overlay surfaces as undefined.
            if (state.target !== null) {
              const t = state.target;
              if (t.avatarUrl === undefined) t.avatarUrl = null;
              if (t.avatarNsfw === undefined) t.avatarNsfw = false;
              if (t.heroUrl === undefined) t.heroUrl = null;
              if (t.heroNsfw === undefined) t.heroNsfw = false;
              if (t.connectedProviders === undefined) t.connectedProviders = [];
            }
          },
      },
    ),
  );
